/**
 * Fuzzy matching utilities: a shared 0-1 string similarity score plus the
 * `FUZZY_MATCH` resolvers that voice-filter generation is built on.
 */

interface MatchableItem {
  id: string;
  long_name: string;
}

interface CommodityItem {
  value: string;
  label: string;
}

interface RankedMatch<T> {
  item: T;
  score: number;
  matchedText: string;
}

/**
 * Calculate Levenshtein distance between two strings.
 * Returns the minimum number of single-character edits needed.
 */
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  // Initialize first row and column
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Calculate normalized Levenshtein similarity (0-1, higher is better)
 */
function levenshteinSimilarity(query: string, target: string): number {
  const distance = levenshteinDistance(query, target);
  const maxLength = Math.max(query.length, target.length);
  if (maxLength === 0) return 1;
  return 1 - (distance / maxLength);
}

/**
 * Count exact word matches between query and target.
 * Returns a score from 0-1 based on how many query words appear in target.
 */
function exactWordMatchScore(query: string, target: string): number {
  const queryWords = query.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
  const targetWords = target.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
  
  if (queryWords.length === 0) return 0;
  
  let matchCount = 0;
  for (const qWord of queryWords) {
    if (targetWords.includes(qWord)) {
      matchCount++;
    }
  }
  
  return matchCount / queryWords.length;
}

/**
 * Calculate hybrid fuzzy match score combining exact word matches and Levenshtein distance.
 * Returns a score from 0-1, where higher is better.
 * 
 * Weights:
 * - 60% exact word matches (order-independent)
 * - 40% Levenshtein character-level similarity
 */
export function calculateMatchScore(query: string, target: string): number {
  const normalizedQuery = query.toLowerCase().trim();
  const normalizedTarget = target.toLowerCase().trim();
  
  // Perfect exact match gets max score
  if (normalizedQuery === normalizedTarget) return 1.0;
  
  const wordScore = exactWordMatchScore(normalizedQuery, normalizedTarget);
  const levenScore = levenshteinSimilarity(normalizedQuery, normalizedTarget);
  
  return (0.6 * wordScore) + (0.4 * levenScore);
}

/**
 * Rank all items by fuzzy match score and return sorted results.
 * Only returns matches with score above threshold.
 */
function rankMatches<T extends { long_name: string } | { label: string }>(
  query: string,
  items: T[],
  textField: 'long_name' | 'label',
  threshold: number = 0.3
): RankedMatch<T>[] {
  const ranked = items.map(item => {
    const text = textField === 'long_name' 
      ? (item as { long_name: string }).long_name 
      : (item as { label: string }).label;
    
    return {
      item,
      score: calculateMatchScore(query ?? "", text ?? ""),
      matchedText: text,
    };
  });
  
  return ranked
    .filter(match => match.score >= threshold)
    .sort((a, b) => b.score - a.score);
}

/**
 * Find the best matching item by fuzzy matching against long_name.
 * Returns the highest-scored match, or undefined if no match above threshold.
 */
export function fuzzyMatchItem(
  query: string,
  items: MatchableItem[]
): MatchableItem | undefined {
  const ranked = rankMatches(query, items, 'long_name');
  return ranked[0]?.item;
}

/**
 * Find the best matching commodity by fuzzy matching against label.
 * Returns the highest-scored match, or undefined if no match above threshold.
 */
export function fuzzyMatchCommodity(
  query: string,
  commodities: CommodityItem[]
): CommodityItem | undefined {
  const ranked = rankMatches(query, commodities, 'label');
  return ranked[0]?.item;
}

/**
 * Recursively process a FilterNode to resolve FUZZY_MATCH directives
 */
export function resolveFuzzyMatches(
  node: any,
  customers: MatchableItem[],
  traders: MatchableItem[],
  commodities: CommodityItem[]
): any {
  if (!node || typeof node !== 'object') return node;

  // Handle arrays (for children)
  if (Array.isArray(node)) {
    return node.map(child => resolveFuzzyMatches(child, customers, traders, commodities));
  }

  // Handle different node types
  if (node.type === 'and' || node.type === 'or') {
    return {
      ...node,
      children: node.children.map((child: any) =>
        resolveFuzzyMatches(child, customers, traders, commodities)
      ),
    };
  }

  if (node.type === 'not') {
    return {
      ...node,
      child: resolveFuzzyMatches(node.child, customers, traders, commodities),
    };
  }

  if (node.type === 'condition') {
    const { field, operator, value } = node;

    // Check if value is a FUZZY_MATCH directive
    if (typeof value === 'string' && value.startsWith('FUZZY_MATCH:')) {
      const query = value.replace('FUZZY_MATCH:', '').trim();

      // Match based on field
      if (field === 'customer_id') {
        const match = fuzzyMatchItem(query, customers);
        return {
          ...node,
          value: match ? match.id : value, // Keep original if no match
        };
      }

      if (field === 'release_data.trader_id') {
        const match = fuzzyMatchItem(query, traders);
        return {
          ...node,
          value: match ? match.id : value,
        };
      }

      if (field === 'commodity_group') {
        const match = fuzzyMatchCommodity(query, commodities);
        return {
          ...node,
          value: match ? match.value : value,
        };
      }
    }

    // Handle arrays of FUZZY_MATCH (for 'in' operator)
    if (Array.isArray(value)) {
      const resolvedValues = value.map(v => {
        if (typeof v === 'string' && v.startsWith('FUZZY_MATCH:')) {
          const query = v.replace('FUZZY_MATCH:', '').trim();

          if (field === 'customer_id') {
            const match = fuzzyMatchItem(query, customers);
            return match ? match.id : v;
          }

          if (field === 'release_data.trader_id') {
            const match = fuzzyMatchItem(query, traders);
            return match ? match.id : v;
          }

          if (field === 'commodity_group') {
            const match = fuzzyMatchCommodity(query, commodities);
            return match ? match.value : v;
          }
        }
        return v;
      });

      return {
        ...node,
        value: resolvedValues,
      };
    }

    return node;
  }

  return node;
}
